import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../config/firebase';
import { Timestamp } from 'firebase/firestore';
import type { Papel, Usuario } from '../tipos';

/** O e-mail é o ID do convite, então normalizar é o que garante a unicidade. */
export const normalizarEmail = (email: string) => email.trim().toLowerCase();

export interface Convite {
  id: string; // o próprio e-mail
  email: string;
  papel: Papel;
  criadoPor: string;
  criadoPorNome?: string;
  criadoEm: unknown;
  /** Timestamp, não string: a regra do Firestore compara direto com request.time. */
  expiraEm: Timestamp;
  aceitoEm: unknown | null;
  enviadoEm?: unknown;
  /**
   * Hash do token que foi por e-mail. Fica no documento, mas a regra do
   * Firestore não deixa o navegador ler o convite de ninguém além do
   * Administrador — e hash não abre porta de todo jeito.
   */
  tokenHash?: string | null;
}

export function useUsuarios() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'usuarios'), orderBy('nome')),
      (s) => {
        setUsuarios(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Usuario));
        setCarregando(false);
      },
      (e) => {
        setErro(e.message);
        setCarregando(false);
      },
    );
  }, []);

  return { usuarios, carregando, erro };
}

export function useConvites() {
  const [convites, setConvites] = useState<Convite[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    return onSnapshot(
      collection(db, 'convites'),
      (s) => {
        setConvites(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Convite));
        setCarregando(false);
      },
      () => setCarregando(false),
    );
  }, []);

  return { convites, carregando };
}

/**
 * Convida e MANDA o e-mail, numa operação só.
 *
 * A gravação mudou de lugar: quem escreve o convite agora é a Cloud Function,
 * porque ela precisa gerar o token, guardar o hash e enviar a mensagem pelo
 * Resend — e a chave do Resend não pode existir no navegador.
 *
 * Se o e-mail não sai, o convite não fica gravado. Antes, convidar só criava um
 * documento e avisar a pessoa era problema do administrador, por fora do
 * sistema; o resultado previsível era convite pendente que ninguém recebeu.
 */
export async function convidar(email: string, papel: Papel): Promise<void> {
  const id = normalizarEmail(email);
  if (!id.includes('@')) throw new Error('E-mail inválido');
  if (papel === 'AGENTE') throw new Error('O papel AGENTE é do Hermes e não se convida por e-mail');

  const chamar = httpsCallable<{ email: string; papel: Papel }, { email: string }>(
    functions,
    'enviarConvite',
  );
  await chamar({ email: id, papel });
}

export async function cancelarConvite(email: string): Promise<void> {
  await deleteDoc(doc(db, 'convites', normalizarEmail(email)));
}

export async function alterarPapel(uid: string, papel: Papel): Promise<void> {
  await updateDoc(doc(db, 'usuarios', uid), { papel });
}

export async function definirAtivo(uid: string, ativo: boolean): Promise<void> {
  await updateDoc(doc(db, 'usuarios', uid), { ativo });
}

export const ROTULO_PAPEL: Record<Papel, string> = {
  ADMINISTRADOR: 'Administrador',
  OPERADOR: 'Operador',
  CONSULTA: 'Consulta',
  AGENTE: 'Agente (Hermes)',
};

export const DESCRICAO_PAPEL: Record<Exclude<Papel, 'AGENTE'>, string> = {
  ADMINISTRADOR: 'Configura o sistema, as integrações e os usuários.',
  OPERADOR: 'Cadastra, cota, analisa e assume oportunidades.',
  CONSULTA: 'Apenas visualiza dados e relatórios.',
};
